import { AsyncPipe, NgTemplateOutlet } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatIconButton } from '@angular/material/button'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInput } from '@angular/material/input'
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu'
import { MatToolbar } from '@angular/material/toolbar'
import { ActivatedRoute } from '@angular/router'
import {
  ElderBasicPaneLayoutComponent,
  ElderHeaderComponent,
  ElderPaneActionsComponent,
  ElderPaneComponent,
  ElderPaneContentComponent,
  ElderPaneHeaderComponent,
  ElderPaneSubtitleComponent,
  ElderPaneTitleComponent,
  ElderThemeDirective,
  ElderToastService,
} from '@elderbyte/ngx-starter'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, Observable } from 'rxjs'
import { IdValidator } from '../../../../../features/validators/id-validator'
import { PropertyKind } from '../../../../properties/property'
import { PropertySpecTableComponent } from '../../../../properties/spec/property-spec-table/property-spec-table.component'
import { WorkStationType } from '../work-station-type'
import { WorkStationTypeService } from '../work-station-type.service'

@Component({
  selector: 'app-work-station-type-detail',
  templateUrl: './work-station-type-detail.component.html',
  styleUrls: ['./work-station-type-detail.component.scss'],
  imports: [
    MatToolbar,
    ElderThemeDirective,
    MatIconButton,
    MatIcon,
    NgTemplateOutlet,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    FormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    PropertySpecTableComponent,
    ElderHeaderComponent,
    AsyncPipe,
    ElderTranslatePipe,
    ElderBasicPaneLayoutComponent,
    ElderPaneComponent,
    ElderPaneHeaderComponent,
    ElderPaneActionsComponent,
    ElderPaneContentComponent,
    ElderPaneTitleComponent,
    ElderPaneSubtitleComponent,
  ],
})
export class WorkStationTypeDetailComponent implements OnInit {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly _workStationType$: BehaviorSubject<WorkStationType> = new BehaviorSubject(null)

  public PropertyKind = PropertyKind

  private _creationMode: boolean

  @Input()
  public embedded = false

  @Output()
  public close: EventEmitter<Event> = new EventEmitter<Event>()

  public readonly idValidatorPattern = IdValidator.pattern

  private workStationTypeService = inject(WorkStationTypeService)

  private toastService = inject(ElderToastService)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const route = inject(ActivatedRoute)
    this.workStationType = route.snapshot.data['type']
  }

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnInit(): void {
    if (!this.workStationType) {
      this.workStationType = this.initNew()
    }
  }

  /***************************************************************************
   *                                                                         *
   * Getter / Setters                                                        *
   *                                                                         *
   **************************************************************************/

  public get workStationType$(): Observable<WorkStationType> {
    return this._workStationType$.asObservable()
  }

  public get workStationType(): WorkStationType {
    return this._workStationType$.getValue()
  }

  @Input()
  public set workStationType(wct: WorkStationType) {
    if (wct) {
      this._creationMode = !wct.id

      this._workStationType$.next(wct)
    }
  }

  public get creationMode(): boolean {
    return this._creationMode
  }

  public get deletionEnabled(): boolean {
    return !this.creationMode
  }

  /***************************************************************************
   *                                                                         *
   * Component Events                                                        *
   *                                                                         *
   **************************************************************************/

  public onSave(event: Event): void {
    this.save()
  }

  public onDelete(event: Event): void {
    this.delete()
  }

  public onClose(event: Event): void {
    this.emitClose(event)
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private save(): void {
    if (this.creationMode && this.workStationType.id) {
      this.workStationTypeService.findById(this.workStationType.id).subscribe({
        next: (_) => this.toastService.info('ID already exists!'),
        error: (_) => this.saveToDB(), // Id does not exist yet -> save
      })
    } else {
      this.saveToDB()
    }
  }

  private saveToDB(): void {
    this.workStationTypeService.save(this.workStationType).subscribe({
      next: (updated) => {
        this.workStationType = updated
        this.toastService.info('messages.persisting_successful')
      },
      error: (err) => this.toastService.error('messages.saving_failed', err),
    })
  }

  private delete(): void {
    this.workStationTypeService.delete(this.workStationType).subscribe({
      next: (_) => {
        this.emitClose()
        this.toastService.info('messages.deleting_successful')
      },
      error: (err) => this.toastService.error('messages.deleting_failed', err),
    })
  }

  private emitClose(event?: Event): void {
    this.close.emit(event)
  }

  private initNew(): WorkStationType {
    return <WorkStationType>{}
  }
}
